import { takeLatest, put, select, call, all } from 'redux-saga/effects';
import { get } from 'lodash';
import { message } from 'antd';
import { MAX_COMPARE_FILTER_BARS } from '@utils/constants';
import * as tabsActions from '@store/dashboards/tabs/actions';
import { currentMetricsMetaSelector } from '@store/common/selectors';
import { selectors as tabsStoreSelector } from '@store/dashboards/tabs';
import { getRulesFromFilter, isDuplicateFilter } from '@utils';
import { compareDataSelector, compareZoomDataSelector } from './selectors';
import * as types from './types';
import { parseQuery, removeFilterFromData } from './utils';

function* getPayload(dashboardId) {
  const tabsStore = yield select(tabsStoreSelector);
  const { tabsData } = tabsStore;
  const payload = get(tabsData, `${dashboardId}`, {});
  return payload;
}

function* addSavedFilters({ filters = [], dashboardId, replaceAll = false }) {
  const payload = yield call(getPayload, dashboardId);
  const config = get(payload, 'config', {});
  const filterBars = get(config, 'filterBars', null);
  const savedFilters = get(config, 'savedFilters', null);

  if (savedFilters && filterBars) {
    const filterBarsLength = get(filterBars, 'length', 0);
    const duplicateFilters = [];
    const newFilters = [];

    if (replaceAll) {
      filters.forEach((filter) => {
        const { name } = filter;
        const filtersObj = getRulesFromFilter(filter.rule, true);

        const filterItem = {
          name,
          filters: filtersObj,
        };

        const itemIndex = filterBars.findIndex((item) =>
          isDuplicateFilter(item.filters, filtersObj),
        );
        if (itemIndex > -1) {
          filterBars.splice(itemIndex, 1);
          duplicateFilters.push(filterItem);
        }

        newFilters.push(filterItem);
      });
    } else {
      filters.forEach((filter) => {
        const filterList = [...savedFilters, ...filterBars];
        const { name } = filter;
        const filtersObj = getRulesFromFilter(filter.rule, true);
        const hasDup = filterList.find((item) => isDuplicateFilter(item.filters, filtersObj));

        if (hasDup) {
          const dupFilter = { ...hasDup };
          if (!dupFilter.name) {
            dupFilter.name = filter.name;
          }
          duplicateFilters.push(dupFilter);
        } else {
          newFilters.push({
            name,
            filters: filtersObj,
          });
        }
      });
    }

    if (duplicateFilters.length > 0) {
      const filterNames = duplicateFilters.map((item) => item.name).join(', ');
      message.warning(
        `${filterNames} ${duplicateFilters.length > 1 ? 'are' : 'is'} already applied.`,
      );
    }

    const combinedFilters = [...savedFilters, ...newFilters];
    const msg = `Cannot apply more than ${MAX_COMPARE_FILTER_BARS} filters`;
    if (replaceAll) {
      if (filterBarsLength + newFilters.length > MAX_COMPARE_FILTER_BARS) {
        message.warning(msg);
        return;
      }
    } else if (filterBarsLength + combinedFilters.length > MAX_COMPARE_FILTER_BARS) {
      message.warning(msg);
      return;
    }

    const updatedConfig = {
      ...config,
      filterBars: [...filterBars],
      savedFilters: replaceAll ? newFilters : combinedFilters,
    };

    yield put(tabsActions.triggerUpdateTab({ ...payload, config: updatedConfig }, dashboardId));
  } else {
    throw new Error('invalid tabs data selected');
  }
}

function* updateZoomInterval({ zoomInterval, dashboardId }) {
  const payload = yield call(getPayload, dashboardId);
  const config = get(payload, 'config', {});

  const zInterval = { ...zoomInterval };
  if (zoomInterval.end === 100) {
    zInterval.to = get(config, 'interval.to', zInterval.to);
  }

  const updatedConfig = {
    ...config,
    zoomInterval: zInterval,
  };

  yield put(tabsActions.triggerUpdateTab({ ...payload, config: updatedConfig }, dashboardId));
}

function* removeFilterByIndex({ index, dashboardId }) {
  const payload = yield call(getPayload, dashboardId);
  const currentMetricsMeta = yield select(currentMetricsMetaSelector);
  const config = get(payload, 'config', {});

  const filterBars = get(config, 'filterBars', []);
  const savedFilters = get(config, 'savedFilters', []);

  if (index < filterBars.length) {
    filterBars.splice(index, 1);
  } else {
    const nextIndex = index - filterBars.length;
    savedFilters.splice(nextIndex, 1);
  }

  const data = yield select(compareDataSelector);
  const zoomData = yield select(compareZoomDataSelector);

  const compareData = removeFilterFromData(data, index);
  const compareZoomData = removeFilterFromData(zoomData, index);

  const queries = yield call(parseQuery, {
    ...config,
    // modify comparison action creates an empty zoom interval object
    interval:
      config.zoomInterval && config.zoomInterval.from ? config.zoomInterval : config.interval,
    allMetrics: currentMetricsMeta,
  });

  yield all([
    put({
      type: types.UPDATE_COMPARE_DATA,
      data: compareData,
    }),
    put({
      type: types.UPDATE_COMPARE_ZOOM_DATA,
      data: compareZoomData,
    }),
    put({
      type: types.SAVE_COMPARE_QUERIES,
      queries,
    }),
  ]);

  yield put(tabsActions.triggerUpdateTab({ ...payload, config: { ...config } }, dashboardId));
}

function* watchAddSavedFilters() {
  yield takeLatest(types.ADD_SAVED_FILTERS, addSavedFilters);
}

function* watchUpdateZoomInterval() {
  yield takeLatest(types.UPDATE_ZOOM_INTERVAL, updateZoomInterval);
}
function* watchRemoveFilterByIndex() {
  yield takeLatest(types.REMOVE_FILTER_BY_INDEX, removeFilterByIndex);
}

export default [watchAddSavedFilters, watchUpdateZoomInterval, watchRemoveFilterByIndex];
